import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
} from 'react';

/**
 * Wave E.1 — vertical drag handle between two panes.
 *
 * Reports the new pane width in pixels via `onChange`. The caller owns
 * the actual pane size (so it can persist to localStorage / clamp /
 * etc). The handle itself is a thin vertical bar with a hover highlight
 * and ew-resize cursor.
 *
 * `direction` controls which pane the new width applies to:
 *   - 'left'  → onChange gets the LEFT pane's new width (drag-right grows it)
 *   - 'right' → onChange gets the RIGHT pane's new width (drag-left grows it)
 *
 * Implementation: pointerdown captures the pointer + initial mouse X,
 * pointermove computes the delta and calls onChange. We listen on
 * window.document during drag so the handle keeps tracking even when
 * the cursor leaves the thin handle area.
 */

export interface DragHandleProps {
  readonly direction: 'left' | 'right';
  readonly currentWidth: number;
  readonly minWidth: number;
  /** Maximum allowed pane width — used to clamp drags. */
  readonly maxWidth: number;
  readonly onChange: (newWidth: number) => void;
  /** Accessible label, e.g. "Resize CTD tree pane". */
  readonly ariaLabel: string;
}

const handleStyle: CSSProperties = {
  width: 6,
  flex: '0 0 6px',
  cursor: 'ew-resize',
  background: 'transparent',
  borderLeft: '1px solid var(--border)',
  borderRight: '1px solid var(--border)',
  position: 'relative',
  zIndex: 1,
  alignSelf: 'stretch',
};

const handleHoverStyle: CSSProperties = {
  background: 'var(--accent-bg, rgba(99,102,241,0.20))',
};

export function DragHandle({
  direction,
  currentWidth,
  minWidth,
  maxWidth,
  onChange,
  ariaLabel,
}: DragHandleProps): ReactElement {
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      dragStateRef.current = { startX: e.clientX, startWidth: currentWidth };
      setDragging(true);
    },
    [currentWidth],
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: globalThis.MouseEvent) => {
      const state = dragStateRef.current;
      if (!state) return;
      const delta = e.clientX - state.startX;
      const candidate =
        direction === 'left' ? state.startWidth + delta : state.startWidth - delta;
      const clamped = Math.max(minWidth, Math.min(maxWidth, candidate));
      onChange(clamped);
    };
    const onUp = () => {
      dragStateRef.current = null;
      setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, direction, minWidth, maxWidth, onChange]);

  // Keyboard fallback: arrow keys nudge by 10px increments.
  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      const step = e.shiftKey ? 40 : 10;
      let delta = 0;
      if (e.key === 'ArrowLeft') delta = direction === 'left' ? -step : step;
      else if (e.key === 'ArrowRight') delta = direction === 'left' ? step : -step;
      else return;
      e.preventDefault();
      const candidate = currentWidth + delta;
      const clamped = Math.max(minWidth, Math.min(maxWidth, candidate));
      onChange(clamped);
    },
    [direction, currentWidth, minWidth, maxWidth, onChange],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(currentWidth)}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      tabIndex={0}
      data-testid={`drag-handle-${direction}`}
      data-dragging={dragging ? 'true' : 'false'}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onKeyDown={handleKeyDown}
      style={{
        ...handleStyle,
        ...(hovered || dragging ? handleHoverStyle : null),
      }}
    />
  );
}
